import * as XLSX from 'xlsx';

/**
 * Exports tabular data to an Excel (.xlsx) file.
 */
export function exportToExcel(
  filename: string,
  sheetName: string,
  headers: string[],
  rows: (string | number | boolean | null | undefined)[][]
): void {
  try {
    const cleanRows = rows.map(r => r.map(cell => (cell == null ? '' : cell)));
    const worksheetData = [headers, ...cleanRows];
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-calculate column widths
    const colWidths = headers.map((h, i) => {
      let maxLen = String(h).length;
      rows.forEach(r => {
        const cellVal = String(r[i] ?? '');
        if (cellVal.length > maxLen) maxLen = cellVal.length;
      });
      return { wch: Math.min(maxLen + 3, 40) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31) || 'Feuille1');

    const cleanFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    XLSX.writeFile(wb, cleanFilename);
  } catch (err) {
    console.error('Error exporting to Excel:', err);
  }
}

/**
 * Exports HTML document content to a Word (.doc) file compatible with Microsoft Word.
 */
export function exportToWord(
  filename: string,
  title: string,
  htmlBody: string,
  isRtl: boolean = false
): void {
  try {
    const wordHtml = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>${title}</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page {
            size: A4 portrait;
            margin: 20mm 15mm 20mm 15mm;
          }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #111;
            direction: ${isRtl ? 'rtl' : 'ltr'};
          }
          h1, h2, h3, h4 {
            margin-top: 6pt;
            margin-bottom: 6pt;
            text-align: center;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 8pt;
            margin-bottom: 12pt;
          }
          th, td {
            border: 1px solid #777;
            padding: 4pt 6pt;
            vertical-align: middle;
            text-align: ${isRtl ? 'right' : 'left'};
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .no-border td {
            border: none !important;
          }
          .text-center {
            text-align: center !important;
          }
          .text-right {
            text-align: right !important;
          }
          .signatures {
            margin-top: 24pt;
            width: 100%;
          }
        </style>
      </head>
      <body dir='${isRtl ? 'rtl' : 'ltr'}'>
        ${htmlBody}
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff', wordHtml], {
      type: 'application/msword;charset=utf-8'
    });

    const cleanFilename = filename.endsWith('.doc') ? filename : `${filename}.doc`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = cleanFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error('Error exporting to Word:', err);
  }
}
